import { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { LeadCategory } from './api'

interface Props {
  categories: LeadCategory[]
  selected: string[]
  onChange: (codes: string[]) => void
}

export default function CategoryMultiSelect({ categories, selected, onChange }: Props) {
  const { t } = useTranslation('leads')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function toggle(code: string) {
    if (selected.includes(code)) {
      onChange(selected.filter(c => c !== code))
    } else {
      onChange([...selected, code])
    }
  }

  const selectedNames = categories.filter(c => selected.includes(c.code))
  // The selection lives in the trigger and the checked boxes, never in pills
  // above it: pills grew the column and made the whole filter bar jump (#627 AC3).
  const label = selected.length === 0
    ? t('common.all')
    : selected.length === 1 && selectedNames.length === 1 ? selectedNames[0].name : t('common.nSelected', { n: selected.length })

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        title={selectedNames.map(c => c.name).join('\n') || undefined}
        className="input-field text-sm w-full text-left flex items-center justify-between gap-1"
      >
        <span className={`truncate ${selected.length ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
          {label}
        </span>
        <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
      </button>
      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {categories.map(c => (
            <label
              key={c.code}
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(c.code)}
                onChange={() => toggle(c.code)}
                className="rounded-sm border-gray-300 dark:border-gray-600"
              />
              <span className="text-gray-700 dark:text-gray-300">{c.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
